import React, { useEffect, useRef } from "react";
import { createUseStyles } from "react-jss";
import Portal from "HOC/Portal";
import { SearchResults } from "Containers/SearchAI";
import SearchIcon from "Assets/images/search_icon.svg";
import CloseIcon from "Assets/images/close.svg";
import styles from "./styles";

interface SearchAIProps {
  onClick: () => void;
  onInputClick: (e: React.MouseEvent<HTMLInputElement>) => void;
  onSearchClick: (e, searchValue: string) => void;
  onChange: (e) => void;
  onClear: (e) => void;
  onKeyDown: (e) => void;
  showResults: boolean;
  searchInputValue: string;
  mapObj: any;
}

const SearchAIPrompt = ({
  onClick,
  onInputClick,
  onSearchClick,
  onChange,
  onClear,
  onKeyDown,
  showResults,
  searchInputValue,
  mapObj,
}: SearchAIProps) => {
  const useStyles = createUseStyles(styles);
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        onClick && onClick();
      }
    });

    const handleTabFromCloseButton = (e: KeyboardEvent) => {
      if (document.activeElement === closeButtonRef.current) {
        if (e.key === "Tab" && !e.shiftKey) {
          e.preventDefault();
          inputRef.current?.focus();
        } else if (e.key === "Tab" && e.shiftKey) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
    };

    const closeButtonEl = closeButtonRef.current;
    closeButtonEl?.addEventListener("keydown", handleTabFromCloseButton);

    return () => {
      closeButtonEl?.addEventListener("keydown", handleTabFromCloseButton);
      document.addEventListener("keydown", () => {});
    };
  }, [onClick]);

  return (
    <Portal>
      <div
        id="close-search-results"
        aria-label="close search results"
        className={classes.container}
      >
        <div className={classes.inputContainer}>
          <SearchIcon
            className={classes.searchIcon}
            onClick={(e) => onSearchClick(e, searchInputValue)}
          />
          <input
            id="search-input"
            ref={inputRef}
            className={classes.searchBar}
            placeholder="What would you like to see?"
            onClick={onInputClick}
            onChange={onChange}
            value={searchInputValue}
            onKeyDown={onKeyDown}
          />
          <button onClick={onClick} className={classes.cancelText}>
            Cancel
          </button>
          {searchInputValue && (
            <button
              aria-label="Clear input text"
              className={classes.closeIcon}
              onClick={onClear}
            >
              <CloseIcon role="button" onClick={onClear} />
            </button>
          )}
        </div>
        {showResults && <SearchResults mapObj={mapObj} />}
      </div>
      <button
        ref={closeButtonRef}
        tabIndex={0}
        aria-label="Close Search Modal"
        className={classes.closeModalIcon}
        onClick={onClick}
      >
        <CloseIcon />
      </button>
    </Portal>
  );
};

export default SearchAIPrompt;
